import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import type { JobListing } from "@endeks/shared";
import { useJobListings, useDeleteJobListing } from "./queries";
import { showDeleteConfirm } from "../../components/Common/DeleteConfirm";
import { QueryError } from "../../components/Common/QueryError";
import { Pagination } from "../../components/Common/Pagination";
import { errorMessage } from "@/lib/error-message";
import { usePagination } from "@/lib/use-pagination";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

// id is optional in the shared schema but the API always returns it for existing rows.
type JobRow = JobListing & { id: string };

export default function JobList() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const { data: rawJobs, isLoading, isError, error, refetch } = useJobListings();
  const jobs: JobRow[] = (rawJobs ?? []) as JobRow[];
  const deleteMutation = useDeleteJobListing();

  const filtered = jobs.filter(
    (j) =>
      !search ||
      (j.title ?? "").toLowerCase().includes(search.toLowerCase()) ||
      (j.department ?? "").toLowerCase().includes(search.toLowerCase()),
  );
  const { pageItems, page, totalPages, totalItems, setPage } = usePagination(filtered, 20, search);

  function handleDelete(id: string) {
    // Başvurusu olan ilanı API reddeder (409); sebebi onError'da gösterilir.
    showDeleteConfirm({
      title: "İlanı Sil",
      description: "Bu ilanı silmek istediğinize emin misiniz? Bu işlem geri alınamaz.",
      onConfirm: () => {
        deleteMutation.mutate(id, {
          onSuccess: () => toast.success("İlan silindi"),
          onError: (e: unknown) => toast.error(errorMessage(e, "Silinemedi")),
        });
      },
    });
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">İş İlanları</h1>
        <Button onClick={() => navigate("/jobs/new")}>
          <Plus className="h-4 w-4 mr-2" />
          Yeni İlan
        </Button>
      </div>

      {/* Search */}
      <div className="relative w-64">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Ara..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-8"
        />
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : isError && !rawJobs ? (
        <QueryError error={error} onRetry={() => void refetch()} />
      ) : filtered.length === 0 ? (
        <div className="flex items-center justify-center h-40 text-muted-foreground text-sm">
          {search ? "Arama sonucu bulunamadı." : "Henüz iş ilanı eklenmedi."}
        </div>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Başlık</TableHead>
                <TableHead>Departman</TableHead>
                <TableHead className="w-32">Lokasyon</TableHead>
                <TableHead className="w-32">Tür</TableHead>
                <TableHead className="w-24">Durum</TableHead>
                <TableHead className="w-28 text-right">İşlemler</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pageItems.map((job) => (
                <TableRow key={job.id}>
                  <TableCell className="font-medium">{job.title}</TableCell>
                  <TableCell className="text-muted-foreground">{job.department}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{job.location}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{job.type}</TableCell>
                  <TableCell>
                    {job.active ? (
                      <Badge variant="default">Aktif</Badge>
                    ) : (
                      <Badge variant="outline">Kapalı</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/jobs/${job.id}/edit`)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => void handleDelete(job.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Pagination
            page={page}
            totalPages={totalPages}
            totalItems={totalItems}
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  );
}
